import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { studentService } from '../services/studentServicepdb';

/**
 * Custom hook for managing student data fetching and updates
 */
export const useStudentData = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false, not true
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const abortControllerRef = useRef(null);
  const lastFetchParamsRef = useRef('');

  // ✅ FIXED: Stable fetch function with proper dependency handling
  const fetchStudentRecords = useCallback(async (searchParams = {}, type = "placementEligible") => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const fetchId = JSON.stringify({ ...searchParams, type });
    // Skip if params haven't changed (deduplication)
    if (fetchId === lastFetchParamsRef.current) {
      console.log("Skipping duplicate fetch request");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await studentService.fetchStudentRecords(
        searchParams,
        abortControllerRef.current.signal,
        type   // ✅ ADD THIS
      );

      if (!abortControllerRef.current.signal.aborted) {
        setData(result.data || []);

        if (result.pagination) {
          setPagination({
            currentPage: result.pagination.currentPage || 1,
            totalPages: result.pagination.totalPages || 1,
            totalRecords: result.pagination.totalRecords || 0,
            recordsPerPage: result.pagination.recordsPerPage || 10,
            hasNextPage: result.pagination.hasNextPage || false,
            hasPrevPage: result.pagination.hasPrevPage || false,
            nextPage: result.pagination.nextPage || null,
            prevPage: result.pagination.prevPage || null
          });
        }

        lastFetchParamsRef.current = fetchId;
      }
    } catch (err) {
      if (err.name === "AbortError") return;

      console.error("Fetch error:", err);
      setError("Failed to fetch student records. Please try again later.");
      setData([]);
    } finally {
      if (!abortControllerRef.current.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  // ✅ Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // --- Existing functions remain unchanged ---
  const updateStudentRecord = async (studentData) => {
    try {
      const updatedRecord = await studentService.updateStudentRecord(studentData);

      setData((prevData) => {
        const newData = [...prevData];
        const index = newData.findIndex((item) => item.id === studentData.id);
        if (index !== -1) newData[index] = { ...updatedRecord };
        return newData;
      });

      return updatedRecord;
    } catch (error) {
      console.error("Error updating student record:", error);
      throw error;
    }
  };

const searchStudents = async (searchField, searchValue, currentFilters = {}, type = "placementEligible") => {
  const searchParams = {
    searchField,
    searchValue,
    page: 1,
    limit: pagination.recordsPerPage || 10,
    ...currentFilters
  };

  return await fetchStudentRecords(searchParams, type);
};

const changePage = async (page, currentFilters = {}, type = "placementEligible") => {
  const searchParams = {
    page,
    limit: pagination.recordsPerPage || 10,
    ...currentFilters
  };

  return await fetchStudentRecords(searchParams, type);
};

const changeItemsPerPage = async (limit, currentFilters = {}, type = "placementEligible") => {
  const searchParams = {
    page: 1,
    limit,
    ...currentFilters
  };

  return await fetchStudentRecords(searchParams, type);
};
  // Initial load
  // useEffect(() => {
  //   fetchStudentRecords({ page: 1, limit: 10 });
  // }, [fetchStudentRecords]);

  return {
    data,
    loading,
    error,
    pagination,
    setPagination,
    fetchStudentRecords,
    updateStudentRecord,
    searchStudents,
    changePage,
    changeItemsPerPage
  };
};

/**
 * Custom hook for managing search and filtering functionality
 */
export const useSearch = (data, selectedColumns, allColumns) => {
  const [searchField, setSearchField] = useState('studentName');
  const [search, setSearch] = useState('');

  // Get searchable columns based on selected columns
  const searchableColumns = useMemo(() => {
    return allColumns.filter(column =>
      selectedColumns.includes(column.key)
    );
  }, [selectedColumns, allColumns]);

  // Filter data based on search criteria
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) {
      console.warn('Data is not an array:', data);
      return [];
    }

    if (!search.trim()) {
      return data;
    }

    return data.filter((item) => {
      const searchValue = search.toLowerCase();
      const fieldValue = item[searchField] ? item[searchField].toString().toLowerCase() : '';
      return fieldValue.includes(searchValue);
    });
  }, [search, searchField, data]);

  // Fixed: Handle field change by accepting the value directly
  const handleFieldChange = (field) => {
    setSearchField(field);
    // Optionally clear search when field changes
    // setSearch('');
  };

  // Fixed: Handle search change by accepting the value directly
  const handleSearchChange = (searchTerm) => {
    setSearch(searchTerm);
  };

  const resetSearch = () => {
    setSearch('');
    setSearchField('studentName');
  };

  return {
    searchField,
    search,
    searchableColumns,
    filteredData,
    handleFieldChange,
    handleSearchChange,
    resetSearch
  };
};

/**
 * Custom hook for managing pagination
 */
export const usePagination = (data, initialItemsPerPage = 10) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  // Calculate pagination values
  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

  // Generate page numbers
  const pageNumbers = useMemo(() => {
    const numbers = [];
    for (let i = 1; i <= totalPages; i++) {
      numbers.push(i);
    }
    return numbers;
  }, [totalPages]);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const goToFirstPage = () => paginate(1);
  const goToLastPage = () => paginate(totalPages);
  const goToNextPage = () => paginate(currentPage + 1);
  const goToPrevPage = () => paginate(currentPage - 1);

  const changeItemsPerPage = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  return {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    currentItems,
    pageNumbers,
    indexOfFirstItem,
    indexOfLastItem,
    paginate,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPrevPage,
    changeItemsPerPage,
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1
  };
};

/**
 * Custom hook for managing column selection
 */
export const useColumnSelection = (permanentColumns, allColumns) => {
  const [selectedColumns, setSelectedColumns] = useState([...permanentColumns]);
  const [tempSelectedColumns, setTempSelectedColumns] = useState([...permanentColumns]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const toggleSelectAll = () => {
    if (selectAll) {
      setTempSelectedColumns([...permanentColumns]);
    } else {
      setTempSelectedColumns(allColumns.map(column => column.key));
    }
    setSelectAll(!selectAll);
  };

  const toggleColumnSelection = (column) => {
    if (permanentColumns.includes(column)) return;

    setTempSelectedColumns(prev =>
      prev.includes(column)
        ? prev.filter(col => col !== column)
        : [...prev, column]
    );
  };

  const confirmColumnSelection = () => {
    setSelectedColumns([...tempSelectedColumns]);
    setIsModalOpen(false);
  };

  const cancelColumnSelection = () => {
    setTempSelectedColumns([...selectedColumns]);
    setIsModalOpen(false);
  };

  const openColumnModal = () => {
    setTempSelectedColumns([...selectedColumns]);
    setIsModalOpen(true);
  };

  const closeColumnModal = () => {
    setIsModalOpen(false);
  };

  // Update selectAll state based on tempSelectedColumns
  useEffect(() => {
    const nonPermanentColumns = allColumns.filter(col => !permanentColumns.includes(col.key));
    const selectedNonPermanent = tempSelectedColumns.filter(col => !permanentColumns.includes(col));
    setSelectAll(selectedNonPermanent.length === nonPermanentColumns.length);
  }, [tempSelectedColumns, permanentColumns, allColumns]);

  return {
    selectedColumns,
    tempSelectedColumns,
    isModalOpen,
    selectAll,
    toggleSelectAll,
    toggleColumnSelection,
    confirmColumnSelection,
    cancelColumnSelection,
    openColumnModal,
    closeColumnModal
  };
};

/**
 * Custom hook for managing edit form state
 */
export const useEditForm = () => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingData, setEditingData] = useState(null);

  const handleEdit = (rowData) => {
    setEditingData({ ...rowData });
    setShowEditForm(true);
  };

  const handleCancel = () => {
    setShowEditForm(false);
    setEditingData(null);
  };

  const handleSave = async (formData, updateFunction) => {
    try {
      await updateFunction(formData);
      setShowEditForm(false);
      setEditingData(null);
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  return {
    showEditForm,
    editingData,
    handleEdit,
    handleCancel,
    handleSave
  };
};

/**
 * Custom hook for managing modal state
 */
export const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(!isOpen);

  return {
    isOpen,
    open,
    close,
    toggle
  };
};

/**
 * Custom hook for local storage with JSON serialization
 */
export const useLocalStorage = (key, initialValue) => {
  // Get initial value from localStorage or use provided initial value
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);

      // Save to localStorage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
};

/**
 * Custom hook for debouncing values (useful for search input)
 */
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook for managing async operations
 */
export const useAsync = (asyncFunction, immediate = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);

  const execute = async (...args) => {
    setLoading(true);
    setError(null);

    try {
      const result = await asyncFunction(...args);
      setData(result);
      return result;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (immediate && asyncFunction) {
      execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate]);

  return {
    data,
    loading,
    error,
    execute
  };
};

/**
 * Custom hook for managing column selection (alias for useColumnSelection)
 */
export const useColumnManagement = (permanentColumns, allColumns) => {
  return useColumnSelection(permanentColumns, allColumns);
};

/**
 * Custom hook combining search and pagination functionality
 */
export const useSearchAndPagination = (data, selectedColumns, allColumns) => {
  // Use search hook
  const {
    searchField,
    search,
    searchableColumns,
    filteredData,
    handleFieldChange,
    handleSearchChange
  } = useSearch(data, selectedColumns, allColumns || []);

  // Use pagination hook with filtered data
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    currentItems,
    indexOfFirstItem,
    paginate,
    changeItemsPerPage
  } = usePagination(filteredData);

  const handleSearch = (e) => {
    handleSearchChange(e.target.value);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    changeItemsPerPage(newItemsPerPage);
  };

  return {
    searchField,
    search,
    currentPage,
    itemsPerPage,
    searchableColumns,
    currentItems,
    totalPages,
    indexOfFirstItem,
    handleFieldChange,
    handleSearch,
    paginate,
    handleItemsPerPageChange
  };
};